/* eslint-disable react-refresh/only-export-components */
import {
  ControllerProps,
  DeepPartial,
  FieldPath,
  FieldValues,
  FormProvider,
  Resolver,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { Overview, Row } from "./overview/overview";
import React from "react";
import {
  asNumber,
  cn,
  extractNotNil,
  getDirty,
  render,
  setForm,
  whenResolved,
} from "@/lib/utils";
import { Button } from "./button";
import { FormField } from "../ui/form";
import { Input } from "../ui/input";
import InlineInput from "./inline-input";
import { isNumber } from "lodash";
import * as YAML from "yaml";
import { Checkbox } from "../ui/checkbox";
import { useTranslation } from "react-i18next";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { DebouncedTextArea, DebouncedTextAreaProps } from "./debounced-input";
import SelectInput from "../select-input/select-input";
import { Loader } from "./loader";
import { EditModeContext } from "./edit-mode";

// export const SelectedKeyRefContext = React.createContext<
//   React.MutableRefObject<HTMLDivElement | null>
// >(undefined as unknown as React.MutableRefObject<HTMLDivElement | null>);

// export const SelectedKeyRefContextProvider = ({
//   children,
//   ref,
// }: {
//   ref?: React.MutableRefObject<HTMLDivElement | null>;
//   children?: React.ReactNode;
// }) => {
//   const _ref = React.useRef<HTMLDivElement | null>(null);
//   const value = ref ?? _ref;
//   return (
//     <SelectedKeyRefContext.Provider value={value}>
//       {children}
//     </SelectedKeyRefContext.Provider>
//   );
// };

// export const useSelectedKeyRef = () => React.useContext(SelectedKeyRefContext);

const EditableDataDisplay = ["overview", "summary", "json", "yaml"] as const;
type EditableDataDisplay = (typeof EditableDataDisplay)[number];
type EditableDataDisplayProps = {
  display?: EditableDataDisplay;
  editable?: boolean;
  displayEditable?: boolean;
};
export const useEditableDataDisplayState = ({
  display: display__ = EditableDataDisplay[0],
  editable: editable_,
  displayEditable: editable = editable_,
}: EditableDataDisplayProps) => {
  const [display_, setDisplay] = React.useState<EditableDataDisplay>(display__);
  const display = editable ? display_ : display__;
  return { display, setDisplay, editable };
};
type EditableDisplayState = ReturnType<typeof useEditableDataDisplayState>;
const EditableDataDisplayContext = React.createContext<EditableDisplayState>(
  undefined as unknown as EditableDisplayState,
);
export function EditableDataDisplayProvider({
  children,
  ...props
}: EditableDataDisplayProps & { children?: React.ReactNode }) {
  return (
    <EditableDataDisplayContext.Provider
      value={useEditableDataDisplayState(props)}
      children={children}
    />
  );
}
export const useEditableDataDisplay = () =>
  React.useContext(EditableDataDisplayContext) ?? {
    display: "overview",
    setDisplay: () => {},
    editable: false,
  };

export type OnSubmit<T = any> = (data: T) => any | Promise<any>;
export const OnSubmitContext = React.createContext<OnSubmit | undefined>(
  undefined as unknown as OnSubmit | undefined,
);
export const OnSubmitProvider = OnSubmitContext.Provider;
export const useOnSubmit = <T,>() =>
  React.useContext(OnSubmitContext) as OnSubmit<T> | undefined;

export type EditableDataProps<T extends FieldValues> = {
  data: T;
  id?: any;
  editable?: boolean;
  editMode?: boolean;
  onSubmit?: (data: T) => any | Promise<any>;
  onChange?: (data?: DeepPartial<T>) => any | Promise<any>;
  loading?: boolean;
  isDefault?: boolean;
  schema?: any;
  resolver?: Resolver<T, any>;
};

export const useEditableDataState = <T extends FieldValues>({
  data,
  id,
  editable,
  editMode: __editMode,
  onSubmit,
  onChange,
  loading,
  isDefault,
  resolver,
}: EditableDataProps<T>) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [current, setCurrent] = React.useState<string | undefined>(undefined);
  const [expanded, setExpanded] = React.useState<(keyof T)[]>([]);
  const [_editMode, setEditMode] = React.useState<boolean>(false);
  const editMode = __editMode ?? (editable ? _editMode : false);
  const form = useForm<T>({ values: isDefault ? data : undefined, resolver });
  const dataRef = useCurrentRef(data);
  const formReset = React.useCallback(
    () => (isDefault ? form.reset() : setForm(form, dataRef.current, true)),
    [form, isDefault],
  );
  const idRef = React.useRef<any>(undefined);
  React.useEffect(() => {
    if (id === undefined || idRef.current !== id) {
      if (data) {
        if (isDefault) {
          form.reset(data, { keepDefaultValues: false });
        } else {
          setForm(form, data);
        }
      }
      if (id) {
        idRef.current = id;
      }
    }
  }, [data, id, idRef, isDefault]);
  const formData = form.watch();

  const _data = editMode ? formData : data;
  return {
    ref,
    current,
    setCurrent,
    expanded,
    setExpanded,
    form,
    formReset,
    data: _data,
    original: data,
    editable,
    editMode,
    setEditMode,
    onSubmit,
    onChange,
    loading,
    isDefault,
  };
};

type EditableDataState<T extends FieldValues> = ReturnType<
  typeof useEditableDataState<T>
>;

export const EditableDataContext = React.createContext<EditableDataState<any>>(
  undefined as unknown as EditableDataState<any>,
);

export const EditableDataProvider = <T extends FieldValues>({
  children,
  ...props
}: EditableDataProps<T> & {
  children?: React.ReactNode;
}) => {
  const value = useEditableDataState<any>(props);
  const form = value.form;
  return (
    <EditableDataContext.Provider value={value}>
      <OnSubmitProvider value={value.onSubmit}>
        <EditModeContext.Provider value={value.editMode}>
          <FormProvider {...form}>{children}</FormProvider>
        </EditModeContext.Provider>
      </OnSubmitProvider>
    </EditableDataContext.Provider>
  );
};

export const useEditableData = <T extends FieldValues = any>() => {
  const data = React.useContext(EditableDataContext) as EditableDataState<T>;
  const onSubmit = useOnSubmit<T>();
  return { ...data, onSubmit };
};

function TextInput({
  field,
  type,
}: Parameters<ControllerProps["render"]>[0] & { type?: "text" | "number" }) {
  return (
    <InlineInput
      type={type}
      value={field.value}
      onSave={async (value) => {
        if (typeof value !== "string") {
          field.onChange(value);
          return;
        }
        if (value.match(/true/i)) {
          return field.onChange(true);
        } else if (value.match(/false/i)) {
          return field.onChange(false);
        }
        const number = asNumber(value);
        if (isNumber(number)) {
          return field.onChange(number);
        }
        try {
          const data = YAML.parse(value);
          return field.onChange(data);
        } catch (ex) {
          //
        }
        return field.onChange(value);
      }}
    />
  );
}

function NumberInput(props: Parameters<ControllerProps["render"]>[0]) {
  return <TextInput type={"number"} {...props} />;
}

function CheckboxInput({ field }: Parameters<ControllerProps["render"]>[0]) {
  return (
    <Checkbox
      checked={field.value}
      onCheckedChange={(value) => field.onChange(value)}
    />
  );
}

function LabelRename({
  field,
  label,
}: Parameters<ControllerProps["render"]>[0] & { label: string }) {
  return (
    <InlineInput
      value={label}
      onSave={async (newLabel) => {
        if (!newLabel) return;
        const next = {
          ...field.value,
          // [label]: undefined,
          // [newLabel]: field.value[label],
        };
        delete next[label];
        next[newLabel] = field.value[label];
        field.onChange(next);
      }}
    />
  );
}

function AddLabel({ field }: Parameters<ControllerProps["render"]>[0]) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex items-center gap-2 col-span-full">
      <Input className="w-full" ref={inputRef} />
      <Button
        onClick={(_ev) => {
          if (!inputRef.current) return;
          const label = inputRef.current.value;
          if (!label) return;
          inputRef.current.value = "";
          const next = { ...field.value, [label]: "" };
          field.onChange(next);
        }}
      >
        + Add
      </Button>
    </div>
  );
}

function RemoveLabel({
  field,
  label,
}: Parameters<ControllerProps["render"]>[0] & { label: string }) {
  return (
    <Button
      onClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const next = {
          ...field.value,
          //  [label]: undefined
        };
        delete next[label];
        field.onChange(next);
      }}
    >
      -
    </Button>
  );
}

function AddIndex({ field }: Parameters<ControllerProps["render"]>[0]) {
  return (
    <Button
      onClick={(_ev) => {
        field.onChange(
          Array.isArray(field.value) ? [...field.value, ""] : [""],
        );
      }}
    >
      + Add
    </Button>
  );
}

function RemoveIndex({
  field,
  label,
}: Parameters<ControllerProps["render"]>[0] & { label: number }) {
  return (
    <Button
      onClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const next = Array.isArray(field.value)
          ? [...field.value.slice(0, label), ...field.value.slice(label + 1)]
          : [];
        field.onChange(next);
      }}
      size={"icon"}
    >
      -
    </Button>
  );
}

function FormFieldTop<TFieldValues extends FieldValues = FieldValues>(
  props: Omit<ControllerProps<TFieldValues, any>, "name" | "control"> & {
    form: UseFormReturn<TFieldValues>;
  },
) {
  return props.render({
    field: {
      name: "",
      onBlur: () => {},
      onChange: (...args: any[]) => {
        const value = args[0];
        setForm(props.form, value, true);
      },
      ref: () => {},
      value: props.form.watch() as any,
    },
    fieldState: {
      invalid: false,
      isDirty: false,
      isTouched: false,
      isValidating: false,
      error: undefined,
    },
    formState: props.form.formState,
  });
}

function FormFieldWrap<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, "control"> & {
  form: UseFormReturn<TFieldValues>;
}) {
  if (!name) {
    return <FormFieldTop {...props} form={form} />;
  }
  return <FormField {...props} name={name} control={form.control} />;
}

export function editableData<T extends FieldValues>(
  props: EditableDataProps<T> &
    EditableDataState<any> &
    EditableDataOverviewProps & { depth?: number; path?: string },
): Row {
  const { current, form } = props;
  if (Array.isArray(props.data)) {
    return {
      accordionProps: {
        className: cn(
          props.path &&
            current == props.path &&
            "bg-yellow-200 dark:bg-yellow-950",
        ),
      },
      rows: [
        ...props.data.map((value, label) => {
          const depth = (props.depth ?? 0) + 1;
          const path = props.path ? `${props.path}.${label}` : `${label}`;
          const next = editableData({
            ...props,
            data: value,
            depth,
            path,
          });
          return {
            label: <div className="flex items-center">{label}</div>,
            accordionProps: {
              className: cn(
                current == path && "bg-yellow-200 dark:bg-yellow-950",
              ),
            },
            ...next,
            value: props.editMode ? (
              <div className="flex items-center gap-2">
                {render(next.value)}
                <div className="ml-auto">
                  <FormFieldWrap
                    name={props.path ?? ""}
                    form={form}
                    render={(props) => <RemoveIndex {...props} label={label} />}
                  />
                </div>
              </div>
            ) : (
              next.value
            ),
          };
        }),
        props.editMode && {
          value: (
            <>
              <FormFieldWrap
                name={props.path ?? ""}
                form={form}
                render={AddIndex}
              />
            </>
          ),
          valueProps: {
            className: cn("col-span-full"),
          },
        },
      ].filter(Boolean) as Row[],
    };
  }
  if (typeof props.data === "object" && props.data !== null) {
    return {
      rows: [
        ...Object.entries(props.data).map(([label, value]) => {
          const depth = (props.depth ?? 0) + 1;
          const path = props.path ? `${props.path}.${label}` : label;
          const next = editableData({
            ...props,
            data: value,
            depth,
            path,
          });
          return {
            label:
              props.editMode && props.labelRename ? (
                <div className="flex items-center">
                  <FormFieldWrap
                    name={props.path ?? ""}
                    form={form}
                    render={(props) => <LabelRename {...props} label={label} />}
                  />
                </div>
              ) : (
                <div className="flex items-center h-full">{label}</div>
              ),
            accordionProps: {
              className: cn(
                current == path && "bg-yellow-200 dark:bg-yellow-950",
              ),
            },
            ...next,
            value: props.editMode ? (
              <div className="flex items-center gap-2">
                {render(next.value)}
                <div className="ml-auto">
                  <FormFieldWrap
                    name={props.path ?? ""}
                    form={form}
                    render={(props) => <RemoveLabel {...props} label={label} />}
                  />
                </div>
              </div>
            ) : (
              next.value
            ),
          };
        }),
        props.editMode && {
          value: (
            <>
              <FormFieldWrap
                name={props.path ?? ""}
                form={form}
                render={AddLabel}
              />
            </>
          ),
          valueProps: {
            className: cn("col-span-full"),
          },
        },
      ].filter(Boolean) as Row[],
    };
  }
  if (typeof props.data === "boolean" && !(props.editMode && props.asText)) {
    if (props.editMode) {
      return {
        value: (
          <FormFieldWrap
            name={props.path ?? ""}
            form={form}
            render={CheckboxInput}
          />
        ),
      };
    }
    return { value: <Checkbox checked={props.data} disabled={true} /> };
  }
  if (typeof props.data === "number" && !(props.editMode && props.asText)) {
    if (props.editMode) {
      return {
        value: (
          <FormFieldWrap
            name={props.path ?? ""}
            form={form}
            render={NumberInput}
          />
        ),
      };
    }
    // return {
    //   value: <Input type="number" value={props.data} disabled={true} />,
    // };
    return { value: <span>{`${props.data}`}</span> };
  }
  //   if (typeof data === "string" || (props.editable && props.asText)) {
  if (props.editMode) {
    return {
      value: (
        <FormFieldWrap name={props.path ?? ""} form={form} render={TextInput} />
      ),
    };
  }
  return { value: <span>{`${props.data}`}</span> };
  // return { value: <Input type="string" value={props.data} disabled={true} /> };
}

export function EditableButtons({ close: close_ }: { close?: () => void }) {
  const {
    editMode,
    setEditMode,
    form,
    loading,
    onChange,
    onSubmit,
    formReset,
  } = useEditableData();
  const { t } = useTranslation();
  const close = React.useMemo(
    () => close_ ?? (() => setEditMode(false)),
    [close_, setEditMode],
  );
  const closeRef = useCurrentRef(close);
  const chain = React.useCallback(
    (cb: (...args: any[]) => any) =>
      (...args: any[]) =>
        whenResolved(cb(args), closeRef.current),
    [],
  );

  return (
    <>
      {editMode && (
        <Button
          type={"button"}
          onClick={chain(() => {
            setEditMode(false);
            formReset();
          })}
          className="bg-inherit text-sm font-normal whitespace-nowrap"
        >
          {t("cancel")}
        </Button>
      )}
      {/* {editMode && (
        <Button
          onClick={() => {
            formReset();
          }}
          className="bg-inherit text-sm font-normal whitespace-nowrap"
        >
          {t("reset")}
        </Button>
      )} */}
      {editMode && !onChange && (
        <Button
          onClick={onSubmit ? chain(form.handleSubmit(onSubmit)) : undefined}
          variant={"azure"}
          className="text-sm font-normal gap-2 items-center flex"
          disabled={!onSubmit}
        >
          {loading && <Loader size="xs" />}
          {t("save")}
        </Button>
      )}
      {editMode && onChange && (
        <Button
          onClick={chain(() => {
            const dirty = getDirty(form);
            const value = extractNotNil(dirty, null);
            return onChange(value);
          })}
          variant={"azure"}
          className="text-sm font-normal gap-2 items-center flex"
          disabled={Object.keys(form.formState.dirtyFields).length <= 0}
        >
          {loading && <Loader size="xs" />}
          {t("update")}
        </Button>
      )}
      {!editMode && (
        <Button
          type={"button"}
          onClick={() => {
            setEditMode(true);
          }}
          variant={"azure"}
          className="text-sm font-normal gap-2 items-center flex"
        >
          {loading && <Loader size="xs" />}
          {t("edit")}
        </Button>
      )}
    </>
  );
}

type EditableDataOverviewProps = { asText?: boolean; labelRename?: boolean };

export function EditableDataOverview(props: EditableDataOverviewProps) {
  const value = useEditableData();
  return <Overview {...editableData({ ...props, ...value })} />;
}

type EditableDataTextAreaProps = {
  indent?: number;
  display?: "json" | "yaml";
} & Omit<
  DebouncedTextAreaProps,
  | "value"
  | "isUndefinable"
  | "forward"
  | "backward"
  | "onChange"
  | "onPending"
  | "disabled"
  | "data"
  | "onSubmit"
  | "id"
>;

export function EditableDataTextArea({
  indent = 2,
  rows = "auto",
  display: display_,
  ...props
}: EditableDataTextAreaProps) {
  const { form, data, editMode } = useEditableData();
  const { display: display__ } = useEditableDataDisplay();
  const display = display_ ?? (display__ as "json" | "yaml");
  const [pending, setPending] = React.useState(false);
  const { forward, backward } = React.useMemo(
    () =>
      display === "yaml"
        ? {
            forward: (data) =>
              YAML.stringify(data, indent ? { indent } : undefined),
            backward: (data) => YAML.parse(data),
          }
        : {
            forward: (data) => JSON.stringify(data, undefined, indent),
            backward: (data) => JSON.parse(data),
          },
    [display, indent],
  );
  return (
    <div className="relative">
      <DebouncedTextArea
        {...props}
        className={cn("min-h-9 h-auto", props?.className)}
        value={data}
        isUndefinable={true}
        debounce={props?.debounce ?? -1}
        forward={forward}
        backward={backward}
        onChange={(value) => {
          form.reset(value);
        }}
        onPending={(data) => setPending(!!data)}
        rows={rows}
        disabled={!editMode}
      />
      {pending && <div className="absolute right-2 top-2">*</div>}
    </div>
  );
}

export type EditableDataRenderProps = EditableDataOverviewProps &
  EditableDataTextAreaProps;

export function EditableDataRender(props: EditableDataRenderProps) {
  const { display } = useEditableDataDisplay();
  if (["overview", "summary"].includes(display)) {
    return <EditableDataOverview {...props} />;
  }
  return <EditableDataTextArea {...props} />;
}

type EditableDataEditableProps = {
  row?: React.ReactNode | React.FC | false;
  rowEnd?: React.ReactNode | React.FC | false;
};

const asOption = (value: EditableDataDisplay) => ({ value, label: value });
export function EditableDisplayToggle() {
  const { display, setDisplay } = useEditableDataDisplay();
  const options = React.useMemo(() => EditableDataDisplay.map(asOption), []);
  return (
    <SelectInput
      value={asOption(display)}
      options={options}
      isMulti={false}
      isClearable={false}
      onChange={(d) => setDisplay(d!.value)}
    />
  );
}

export function EditableDataEditable({
  row,
  rowEnd,
  children,
}: EditableDataEditableProps & { children?: React.ReactNode }) {
  const display = useEditableDataDisplay();
  const { onSubmit, editable } = useEditableData();
  return (row ?? (rowEnd || (onSubmit ?? editable))) ? (
    <div className="flex flex-col gap-1">
      <div className="flex items-center">
        {render(row)}
        {(rowEnd ?? onSubmit ?? editable ?? display?.editable) && (
          <div className="ml-auto flex items-center gap-2">
            {render(rowEnd)}
            {display?.editable && <EditableDisplayToggle />}
            {(onSubmit ?? editable) && <EditableButtons />}
          </div>
        )}
      </div>
      {children}
    </div>
  ) : (
    children
  );
}

export function EditableData<T extends FieldValues>({
  children,
  ...props
}: EditableDataProps<T> &
  EditableDataDisplayProps &
  EditableDataEditableProps &
  EditableDataRenderProps & { children?: React.ReactNode }) {
  return (
    <EditableDataDisplayProvider {...props}>
      <EditableDataProvider {...props}>
        <EditableDataEditable {...props}>
          <EditableDataRender {...props} />
          {children}
        </EditableDataEditable>
      </EditableDataProvider>
    </EditableDataDisplayProvider>
  );
}
